'use client';

import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';
import { globalErrorCopy } from './globalErrorCopy';
import { CHUNK_RELOAD_KEY, isChunkLoadError, shouldReloadOnChunkError } from '@/lib/chunk-reload';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Read the URL locale after mount so the server and first client render match (default locale).
  const [copy, setCopy] = useState(() => globalErrorCopy(''));
  useEffect(() => {
    setCopy(globalErrorCopy(window.location.pathname));
  }, []);

  useEffect(() => {
    // Stale-deploy recovery — hard-reload once on a ChunkLoadError before
    // reporting (cooldown-guarded against a reload loop).
    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
      const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || null;
      if (shouldReloadOnChunkError(error, Date.now(), last)) {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }
    posthog.captureException(error);
    Sentry.captureException(error);
  }, [error]);

  return (
    // global-error must include html and body tags
    <html lang={copy.locale} translate="no" className="notranslate">
      <body>
        {/* Root CSS is not loaded here, so the markup is self-styled. */}
        <div
          role="alert"
          style={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{copy.title}</h2>
          <p style={{ maxWidth: 420, fontSize: 14, margin: 0 }}>{copy.description}</p>
          <button type="button" onClick={reset} style={{ padding: '8px 16px', fontSize: 14 }}>
            {copy.retry}
          </button>
        </div>
      </body>
    </html>
  );
}
